import React, { useEffect, useState } from 'react';
import { Card, CardHeader, Table, Btn, Badge, Modal, FormRow, FormField, SearchBar, Spinner } from '../components/ui/Card';
import ProjectDetail, { TEMPLATES } from '../components/ProjectDetail';
import api from '../utils/api';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';

const STATUTS = ['planning', 'active', 'completed', 'delayed', 'cancelled'];
const STATUTS_FR = { planning: 'Planification', active: 'En cours', completed: 'Terminé', delayed: 'En retard', cancelled: 'Annulé' };
const STATUTS_COLORS = { planning: 'info', active: 'success', completed: 'gold', delayed: 'error', cancelled: 'default' };

const defaultForm = { name: '', description: '', status: 'planning', start_date: '', end_date: '', budget: 0, coordinator: '' };

export default function ProjectsPage() {
  const { hasRole } = useAuth();
  const canEdit   = hasRole('super_admin','admin','assistant_admin','rh','direction');
  const canDelete = hasRole('super_admin','admin','assistant_admin','rh','direction');
  const [projects,     setProjects]     = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [search,       setSearch]       = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [modalOpen,    setModalOpen]    = useState(false);
  const [editing,      setEditing]      = useState(null);
  const [form,         setForm]         = useState(defaultForm);
  const [saving,       setSaving]       = useState(false);
  const [detailProject, setDetailProject] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [showLibrary,  setShowLibrary]  = useState(false);

  const load = async (signal) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (filterStatus) params.append('status', filterStatus);
      const r = await api.get(`/projects?${params}`);
      if (!signal?.aborted) setProjects(r.data.data || []);
    } catch { if (!signal?.aborted) toast.error('Erreur de chargement'); }
    finally { if (!signal?.aborted) setLoading(false); }
  };

  useEffect(() => {
    document.getElementById('page-title') && (document.getElementById('page-title').textContent = 'Gestion des Projets');
    const ctrl = new AbortController();
    load(ctrl.signal);
    return () => ctrl.abort();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    const ctrl = new AbortController();
    load(ctrl.signal);
    return () => ctrl.abort();
  }, [search, filterStatus]); // eslint-disable-line react-hooks/exhaustive-deps

  const openModal = (p = null) => {
    setEditing(p);
    setSelectedTemplate('');
    setForm(p ? {
      name: p.name || '', description: p.description || '', status: p.status || 'planning',
      start_date: p.start_date ? p.start_date.split('T')[0] : '',
      end_date: p.end_date ? p.end_date.split('T')[0] : '',
      budget: p.budget_total || p.budget || 0, coordinator: p.coordinator || p.responsable || '',
    } : defaultForm);
    setModalOpen(true);
  };

  const applyTemplate = (key) => {
    const tpl = TEMPLATES[key];
    if (!tpl) return;
    setSelectedTemplate(key);
    setForm(prev => ({
      ...prev,
      name: prev.name || tpl.label.replace(/^[^ ]+ /, ''),
      description: prev.description || tpl.description,
    }));
  };

  const handleSave = async e => {
    e.preventDefault(); setSaving(true);
    try {
      let result;
      if (editing) {
        const r = await api.put(`/projects/${editing.id}`, form);
        result = r.data.data;
        toast.success('Mis à jour');
      } else {
        const r = await api.post('/projects', form);
        result = r.data.data;
        toast.success('Projet créé');
        // If a template was selected, apply phases/objectives immediately
        if (selectedTemplate && result?.id) {
          const tpl = TEMPLATES[selectedTemplate];
          const phases = tpl.phases.map((ph, idx) => ({
            id: `${Date.now()}_${idx}`,
            name: ph.name,
            icon: ph.icon,
            start_date: '',
            end_date: '',
            budget: 0,
            budget_used: 0,
            tasks: ph.tasks.map((name, ti) => ({ id: `${Date.now()}_${idx}_${ti}`, name, done: false })),
          }));
          await api.put(`/projects/${result.id}`, {
            phases: JSON.stringify(phases),
            objectives: tpl.objectives,
            team_members: tpl.team_members,
            risks: JSON.stringify(tpl.risks.map((r, i) => ({ ...r, id: `r_${i}` }))),
          });
        }
      }
      setModalOpen(false); load();
    } catch (err) { toast.error(err.response?.data?.message || 'Erreur'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Supprimer définitivement le projet "${name}" ?\n\nToutes les données associées (phases, tâches, budget) seront perdues.`)) return;
    try {
      await api.delete(`/projects/${id}`);
      toast.success('Projet supprimé');
      if (detailProject?.id === id) setDetailProject(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Erreur lors de la suppression');
    }
  };

  const handleUpdateDetail = async (id, data) => {
    try {
      const r = await api.put(`/projects/${id}`, data);
      toast.success('Projet mis à jour');
      load();
      // Refresh open detail
      if (detailProject?.id === id) setDetailProject(r.data.data);
    } catch (err) { toast.error(err.response?.data?.message || 'Erreur'); throw err; }
  };

  const f = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const columns = [
    {
      key: 'name', header: 'Projet', render: (v, r) => (
        <div>
          <p style={{ fontWeight: 600, color: 'var(--navy)' }}>{v}</p>
          <p style={{ fontSize: '0.72rem', color: 'var(--gray-400)', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.description || ''}</p>
        </div>
      ),
    },
    { key: 'status', header: 'Statut', render: v => <Badge type={STATUTS_COLORS[v] || 'default'}>{STATUTS_FR[v] || v}</Badge> },
    {
      key: 'progress', header: 'Avancement', render: (v, r) => (
        <div style={{ minWidth: 100 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.72rem', marginBottom: 3 }}>
            <span style={{ fontWeight: 600 }}>{v || 0}%</span>
          </div>
          <div style={{ height: 6, background: 'var(--gray-200)', borderRadius: 99, overflow: 'hidden' }}>
            <div style={{ height: '100%', width: `${v || 0}%`, background: r.status === 'delayed' ? '#dc2626' : '#10b981', borderRadius: 99, transition: 'width 0.3s' }} />
          </div>
        </div>
      ),
    },
    {
      key: 'budget_total', header: 'Budget', render: (v, r) => (
        <div style={{ fontSize: '0.82rem' }}>
          <p style={{ fontWeight: 600 }}>{parseFloat(v || 0).toLocaleString('fr-FR')} Ar</p>
          <p style={{ color: 'var(--gray-400)' }}>{parseFloat(r.budget_used || 0).toLocaleString('fr-FR')} Ar utilisés</p>
        </div>
      ),
    },
    {
      key: 'start_date', header: 'Période', render: (v, r) => (
        <div style={{ fontSize: '0.8rem' }}>
          <p>{v ? (([y,m,d])=>new Date(y,m-1,d).toLocaleDateString('fr-FR'))(String(v).slice(0,10).split('-').map(Number)) : '—'}</p>
          <p style={{ color: 'var(--gray-400)' }}>{r.end_date ? (([y,m,d])=>new Date(y,m-1,d).toLocaleDateString('fr-FR'))(String(r.end_date).slice(0,10).split('-').map(Number)) : '—'}</p>
        </div>
      ),
    },
    {
      key: 'actions', header: 'Actions', render: (_, row) => (
        <div style={{ display: 'flex', gap: 5 }}>
          <Btn size="sm" variant="primary" onClick={() => setDetailProject(row)} title="Voir le détail complet">📊</Btn>
          {canEdit && <Btn size="sm" variant="secondary" onClick={() => openModal(row)}>✏️</Btn>}
          {canDelete && <Btn size="sm" variant="danger" onClick={() => handleDelete(row.id, row.name)} title="Supprimer définitivement">🗑️</Btn>}
        </div>
      ),
    },
  ];

  return (
    <div className="animate-fade">
      {/* Header card */}
      <Card style={{ marginBottom: 16 }}>
        <CardHeader
          title="Gestion des Projets & Activités"
          subtitle={`${projects.length} projet(s)`}
          action={
            <div style={{ display: 'flex', gap: 8 }}>
              <Btn variant="secondary" onClick={() => setShowLibrary(v => !v)}>
                📚 {showLibrary ? 'Masquer' : 'Bibliothèque'}
              </Btn>
              {canEdit && <Btn variant="gold" onClick={() => openModal()}>➕ Nouveau projet</Btn>}
            </div>
          }
        />
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 180 }}><SearchBar value={search} onChange={setSearch} placeholder="Rechercher un projet..." /></div>
          <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={{ width: 'auto', minWidth: 150 }}>
            <option value="">Tous les statuts</option>
            {STATUTS.map(s => <option key={s} value={s}>{STATUTS_FR[s]}</option>)}
          </select>
        </div>
      </Card>

      {/* Template library */}
      {showLibrary && (
        <Card style={{ marginBottom: 16 }} className="animate-fade">
          <h3 style={{ fontFamily: "'Playfair Display', serif", fontSize: '1rem', color: 'var(--navy)', marginBottom: 4 }}>📚 Bibliothèque de Modèles</h3>
          <p style={{ fontSize: '0.78rem', color: 'var(--gray-500)', marginBottom: 14 }}>Cliquez sur un modèle pour créer un nouveau projet pré-configuré.</p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 }}>
            {Object.entries(TEMPLATES).map(([key, tpl]) => (
              <div key={key}
                onClick={() => { if (canEdit) { openModal(); setTimeout(() => applyTemplate(key), 50); } }}
                style={{ border: '1.5px solid var(--gray-200)', borderRadius: 10, padding: '14px 16px', cursor: canEdit ? 'pointer' : 'default', transition: 'all 0.15s', background: 'var(--gray-50)' }}
                onMouseEnter={e => { if (canEdit) e.currentTarget.style.borderColor = 'var(--gold)'; e.currentTarget.style.background = 'rgba(201,168,76,0.04)'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--gray-200)'; e.currentTarget.style.background = 'var(--gray-50)'; }}>
                <div style={{ fontSize: '1.4rem', marginBottom: 6 }}>{tpl.label.split(' ')[0]}</div>
                <div style={{ fontWeight: 700, fontSize: '0.84rem', color: 'var(--navy)', marginBottom: 4 }}>{tpl.label.slice(tpl.label.indexOf(' ') + 1)}</div>
                <p style={{ fontSize: '0.73rem', color: 'var(--gray-500)', lineHeight: 1.5, marginBottom: 8 }}>{tpl.description}</p>
                <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
                  <span style={{ background: 'var(--gray-200)', color: 'var(--gray-600)', fontSize: '0.65rem', padding: '2px 7px', borderRadius: 20 }}>
                    {tpl.phases.length} phases
                  </span>
                  <span style={{ background: 'var(--gray-200)', color: 'var(--gray-600)', fontSize: '0.65rem', padding: '2px 7px', borderRadius: 20 }}>
                    {tpl.phases.reduce((s, p) => s + p.tasks.length, 0)} tâches
                  </span>
                  <span style={{ background: 'var(--gray-200)', color: 'var(--gray-600)', fontSize: '0.65rem', padding: '2px 7px', borderRadius: 20 }}>
                    {tpl.risks.length} risques
                  </span>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Projects table */}
      <Card>{loading ? <Spinner /> : <Table columns={columns} data={projects} emptyMessage="Aucun projet enregistré" />}</Card>

      {/* ── Detail Modal ── */}
      <Modal open={!!detailProject} onClose={() => setDetailProject(null)} title={`📊 Détail — ${detailProject?.name || ''}`} width={780}>
        {detailProject && (
          <ProjectDetail
            project={detailProject}
            canEdit={canEdit}
            onUpdate={handleUpdateDetail}
            onClose={() => setDetailProject(null)}
          />
        )}
      </Modal>

      {/* ── Create/Edit Modal ── */}
      <Modal open={modalOpen} onClose={() => setModalOpen(false)} title={editing ? `✏️ Modifier — ${editing.name}` : '➕ Nouveau projet'} width={660}>
        <form onSubmit={handleSave} style={{ padding: 24 }}>

          {/* Template picker (only on create) */}
          {!editing && (
            <div style={{ marginBottom: 18 }}>
              <label style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--gray-600)', textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', marginBottom: 8 }}>Partir d'un modèle (optionnel)</label>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 7 }}>
                {[{ key: '', label: '🆕 Projet vide' }, ...Object.entries(TEMPLATES).map(([k, v]) => ({ key: k, label: v.label }))].map(tpl => (
                  <button key={tpl.key} type="button" onClick={() => tpl.key ? applyTemplate(tpl.key) : setSelectedTemplate('')}
                    style={{
                      padding: '8px 12px', borderRadius: 8, fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer', textAlign: 'left',
                      border: `1.5px solid ${selectedTemplate === tpl.key ? 'var(--gold)' : 'var(--gray-200)'}`,
                      background: selectedTemplate === tpl.key ? 'rgba(201,168,76,0.08)' : 'white',
                      color: selectedTemplate === tpl.key ? 'var(--gold-dark)' : 'var(--gray-600)',
                    }}>
                    {selectedTemplate === tpl.key ? '✓ ' : ''}{tpl.label}
                  </button>
                ))}
              </div>
              {selectedTemplate && (
                <div style={{ marginTop: 8, padding: '8px 12px', background: 'rgba(201,168,76,0.07)', border: '1px solid rgba(201,168,76,0.3)', borderRadius: 7, fontSize: '0.75rem', color: 'var(--gold-dark)' }}>
                  ✓ Modèle "{TEMPLATES[selectedTemplate]?.label}" sélectionné — phases, tâches, objectifs et risques seront créés automatiquement.
                </div>
              )}
              <hr style={{ border: 'none', borderTop: '1px solid var(--gray-200)', margin: '16px 0 0' }} />
            </div>
          )}

          <FormRow cols={1}><FormField label="Nom du projet *"><input value={form.name} onChange={e => f('name', e.target.value)} required /></FormField></FormRow>
          <FormRow cols={1}><FormField label="Description"><textarea value={form.description} onChange={e => f('description', e.target.value)} rows={2} style={{ resize: 'vertical' }} /></FormField></FormRow>
          <FormRow>
            <FormField label="Statut">
              <select value={form.status} onChange={e => f('status', e.target.value)}>
                {STATUTS.map(s => <option key={s} value={s}>{STATUTS_FR[s]}</option>)}
              </select>
            </FormField>
            <FormField label="Coordinateur / Responsable"><input value={form.coordinator} onChange={e => f('coordinator', e.target.value)} /></FormField>
          </FormRow>
          <FormRow>
            <FormField label="Date de début"><input type="date" value={form.start_date} onChange={e => f('start_date', e.target.value)} /></FormField>
            <FormField label="Date de fin"><input type="date" value={form.end_date} onChange={e => f('end_date', e.target.value)} /></FormField>
          </FormRow>
          <FormRow cols={1}><FormField label="Budget total (Ar)"><input type="number" value={form.budget} onChange={e => f('budget', e.target.value)} /></FormField></FormRow>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 8 }}>
            <Btn type="button" variant="secondary" onClick={() => setModalOpen(false)}>Annuler</Btn>
            <Btn type="submit" variant="gold" disabled={saving}>{saving ? 'Enregistrement...' : editing ? '💾 Mettre à jour' : '➕ Créer le projet'}</Btn>
          </div>
        </form>
      </Modal>
    </div>
  );
}
